import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../../components/Logo';
import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { auth } from '../../config/firebase';
import InputField from '../../components/common/InputField';
import PrimaryButton from '../../components/common/PrimaryButton';
import TestimonialCard from '../../components/onboarding/TestimonialCard';
import {
  Container,
  FormSection,
  TestimonialSection,
  Title,
  Form,
  FormFooter,
  Link,
  LogoWrapper,
  BrandText
} from '../../styles/components';

const googleProvider = new GoogleAuthProvider();

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Monitor auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('User is signed in:', user.email);
        navigate('/dashboard');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError(null);
    setResetEmailSent(false);
  };

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      console.log('Successfully logged in:', userCredential.user.email);
    } catch (error) {
      console.error('Login error:', error);
      setError(getErrorMessage(error.code));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Successfully logged in with Google:', result.user.email);
    } catch (error) {
      console.error('Google login error:', error);
      setError(getErrorMessage(error.code));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email address to reset your password.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      await sendPasswordResetEmail(auth, formData.email);
      setResetEmailSent(true);
      setError(null);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(getErrorMessage(error.code));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/popup-closed-by-user':
        return 'Sign in was cancelled.';
      default:
        return 'An error occurred during sign in. Please try again.';
    }
  };

  return (
    <Container>
      <FormSection>
        <LogoWrapper>
          <Logo />
        </LogoWrapper>
        <Title>Welcome back to <BrandText>Planet Protection</BrandText></Title>
        <Form onSubmit={handleEmailPasswordLogin}>
          {error && (
            <div style={{ color: 'red', marginBottom: '1rem' }}>
              {error}
            </div>
          )}
          {resetEmailSent && (
            <div style={{ color: 'green', marginBottom: '1rem' }}>
              Password reset email sent! Please check your inbox.
            </div>
          )}
          <InputField
            label="Email"
            type="email"
            name="email"
            placeholder="name@company.com"
            required
            value={formData.email}
            onChange={handleInputChange}
          />
          <InputField
            label="Password"
            type="password"
            name="password"
            placeholder="Enter your password"
            required
            value={formData.password}
            onChange={handleInputChange}
          />
          <div style={{ textAlign: 'left', marginBottom: '1rem' }}>
            <Link 
              href="#" 
              onClick={handlePasswordReset}
              style={{ fontSize: '0.875rem' }}
            >
              Forgot password?
            </Link>
          </div>
          <PrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </PrimaryButton>
          
          <div style={{ margin: '1rem 0', textAlign: 'center', position: 'relative' }}>
            <div style={{ 
              borderBottom: '1px solid #e5e7eb', 
              position: 'absolute', 
              top: '50%', 
              width: '100%', 
              zIndex: 1 
            }} />
            <span style={{ 
              backgroundColor: '#fff', 
              padding: '0 1rem', 
              color: '#6b7280',
              position: 'relative',
              zIndex: 2
            }}>
              or
            </span>
          </div>
          
          <button 
            type="button" 
            onClick={handleGoogleLogin}
            disabled={isSubmitting}
            style={{
              width: '100%',
              height: '40px',
              border: '1px solid #dadce0',
              borderRadius: '8px',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 16px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#3c4043',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
          >
            <img 
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
              alt="Google logo" 
              style={{ marginRight: '8px', width: '18px', height: '18px' }}
            />
            Continue with Google
          </button>
          
          <FormFooter>
            Don't have an account? <Link href="/onboarding/step-one">Sign up</Link>
          </FormFooter>
        </Form>
      </FormSection>
      <TestimonialSection>
        {/* <TestimonialCard
          quote="Finally, an insurance partner that makes sense for our values. Their platform is refreshingly simple to use, and we can finally see what our insurance impacts. As a B-Corporation, we love that our insurance now actively supports environmental initiatives while providing excellent coverage."
          authorName="Louiza Ziane"
          authorRole="Co-founder, Toast Brewing"
          authorInitials="LZ"
        /> */}
      </TestimonialSection>
    </Container>
  );
};

export default Login; 